import React from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LAST_UPDATED = "February 7, 2026";
const VERSION = "2026-02-07";

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`privacy-section-${n}`}>
    <h2 className="font-serif-display text-2xl text-slate-900 mb-3">
      <span className="font-mono-tech text-sm text-slate-500 mr-3">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Privacy() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      {user ? <AppHeader /> : (
        <header className="border-b border-slate-200">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4">
              <ChevronLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>
      )}
      <main className="max-w-[820px] mx-auto px-6 py-12 md:py-16 flex-1 w-full" data-testid="privacy-page">
        <div className="label-tiny mb-3">Legal</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-slate-900">Privacy Policy</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <span data-testid="privacy-last-updated">Last updated: <span className="tabular-nums text-slate-900 font-medium">{LAST_UPDATED}</span></span>
          <span aria-hidden="true">·</span>
          <span className="font-mono-tech text-xs">Version {VERSION}</span>
        </div>

        <div className="border-t border-slate-200 mt-8 pt-10">
          <Section n={1} title="Introduction">
            <p>
              This Privacy Policy explains how ProSe.Claims (operated by MisherTech) collects,
              uses, discloses, and safeguards information about you when you use the platform.
              We take privacy seriously because the information you provide is sensitive.
            </p>
          </Section>

          <Section n={2} title="Information We Collect">
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account information:</strong> name, email address, profile image (via Google OAuth).</li>
              <li><strong>Case content:</strong> facts, parties, timeline, evidence, notes, drafts, and reports you create or upload.</li>
              <li><strong>Usage information:</strong> pages viewed, features used, timestamps, IP address, browser and device type.</li>
              <li><strong>Consent records:</strong> policy version, acceptance date and time, and user identifier for audit purposes.</li>
            </ul>
            <p className="text-xs text-slate-500">
              ProSe.Claims is currently free during our Research &amp; Development phase. We
              do not collect payment or billing information while the platform is free.
            </p>
          </Section>

          <Section n={3} title="How We Use Information">
            <ul className="list-disc pl-6 space-y-1">
              <li>To operate, secure, and improve the platform.</li>
              <li>To generate and export the documents and reports you request.</li>
              <li>To comply with legal obligations and enforce our Terms of Use.</li>
              <li>To communicate about your account and, if you have opted in separately, to send marketing messages.</li>
            </ul>
          </Section>

          <Section n={4} title="AI Processing">
            <p>
              When you use AI drafting features, the content of your case (facts, timeline,
              evidence descriptions) is transmitted to our AI provider (OpenAI GPT-5.2 via
              Emergent) to generate draft text. We do not use your case content to train
              third-party models. Drafts are working documents that require independent
              review.
            </p>
          </Section>

          <Section n={5} title="Sharing With Third Parties">
            <p>We share information only with service providers that help us run the platform:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Google (OAuth):</strong> account authentication.</li>
              <li><strong>OpenAI (via Emergent):</strong> AI drafting on your request.</li>
              <li><strong>Emergent Object Storage:</strong> secure storage of your uploaded evidence.</li>
              <li><strong>MongoDB Atlas:</strong> database hosting.</li>
            </ul>
            <p>
              We do not sell your personal information. We may disclose information if
              required by law, subpoena, or court order, and to protect the rights, safety,
              or property of MisherTech or others.
            </p>
          </Section>

          <Section n={6} title="Data Retention and Deletion">
            <p>
              You can request deletion of your account at any time from the Account page.
              Deletion initiates a 30-day recovery window during which the data is retained
              but inaccessible; after 30 days, the data is permanently erased, subject to
              limited retention required by law.
            </p>
          </Section>

          <Section n={7} title="Security">
            <p>
              We use HTTPS, encrypted at-rest storage where available, allowlisted MIME types
              for uploads, size limits, and access controls. No system is perfectly secure.
              Notify us promptly if you suspect any compromise of your account.
            </p>
          </Section>

          <Section n={8} title="Retention">
            <p>
              We retain your account information and case content for the life of your
              account and for a reasonable period afterward for legal, audit, and dispute
              resolution purposes. See our Retention Policy for specifics. You may request
              deletion by contacting us.
            </p>
          </Section>

          <Section n={9} title="Your Rights">
            <p>
              Depending on where you live, you may have rights to access, correct, delete,
              or export your personal information, and to object to or restrict certain
              processing. Contact
              {" "}<a href="mailto:corporate@mishertech.com" className="underline underline-offset-4">corporate@mishertech.com</a>{" "}
              to exercise these rights.
            </p>
          </Section>

          <Section n={10} title="Children">
            <p>ProSe.Claims is not directed to children under 18 and we do not knowingly collect information from them.</p>
          </Section>

          <Section n={11} title="Cookies and Tracking">
            <p>
              We use a single first-party session cookie to keep you logged in, and internal
              analytics events to measure platform usage. We do not embed third-party
              advertising or cross-site tracking.
            </p>
          </Section>

          <Section n={12} title="International Transfers">
            <p>Our servers are located in the United States. If you access the platform from outside the U.S., your information will be transferred to and processed here.</p>
          </Section>

          <Section n={13} title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make a material
              change, we will require you to review and acknowledge the updated Policy
              before continuing to use the platform.
            </p>
          </Section>

          <Section n={14} title="Contact">
            <p>
              Questions about this Privacy Policy may be directed to
              {" "}<a href="mailto:corporate@mishertech.com" className="underline underline-offset-4">corporate@mishertech.com</a>.
            </p>
          </Section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
